<script setup lang="ts">
import { CssVarColors } from "@/shared/themes/CssVarColors.interface";
import { PageBlock } from "@/views/blocks/PageBlock.interface";
import { FeaturesBaseProps } from "./features.interfaces";
import { computed } from "vue";
import EditableElement from "@/views/page/EditableElement.vue";
import TagComponent from "@/views/blocks/features/shared/tag/TagComponent.vue";
import TitleComponent from "@/views/blocks/features/shared/title/TitleComponent.vue";
import DescriptionComponent from "@/views/blocks/features/shared/description/DescriptionComponent.vue";
import FeaturesComponent from "@/views/blocks/features/shared/features/FeaturesComponent.vue";
import FeatureComponent from "@/views/blocks/features/shared/features/FeatureComponent.vue";

const props = defineProps<{
    block: PageBlock<FeaturesBaseProps>;
    pageColors: CssVarColors;
}>();

const dataValues = computed(() => props.block.dataValues);
const styleValues = computed(() => props.block.styleValues);
</script>

<template>
    <div
        :id="block.webId"
        :style="{ backgroundColor: styleValues.background }"
        class="md:flex py-24 sm:py-32"
    >
        <div class="md:w-1/2 flex justify-center items-center">
            <div class="max-w-2xl p-6 ml-auto">
                <EditableElement
                    v-if="!dataValues.tag.isHidden"
                    :blockId="block.id"
                    field-name="tag"
                >
                    <TagComponent :text="dataValues.tag.data.text" :textColor="styleValues.brand" />
                </EditableElement>
                <EditableElement
                    v-if="!dataValues.title.isHidden"
                    :blockId="block.id"
                    field-name="title"
                >
                    <TitleComponent
                        :text="dataValues.title.data.text"
                        :textColor="styleValues.text"
                        :tag="dataValues.title.data.tag"
                    />
                </EditableElement>
                <EditableElement
                    v-if="!dataValues.description.isHidden"
                    :blockId="block.id"
                    field-name="description"
                >
                    <DescriptionComponent
                        :text="dataValues.description.data.text"
                        :textColor="styleValues.text"
                    />
                </EditableElement>
                <FeaturesComponent>
                    <EditableElement
                        v-for="feature in dataValues.features.data.items"
                        :key="feature.id"
                        :blockId="block.id"
                        :field-name="`features_${feature.id}`"
                        :hideable="false"
                    >
                        <FeatureComponent
                            :title="feature.title"
                            :description="feature.description"
                            :text-color="styleValues.text"
                            :icon-src="feature.iconSrc"
                        />
                    </EditableElement>
                </FeaturesComponent>
            </div>
        </div>
        <div class="flex justify-center items-center md:ml-auto md:w-1/2 pl-6">
            <EditableElement
                v-if="!dataValues.image.isHidden"
                :blockId="block.id"
                field-name="image"
            >
                <img
                    :src="dataValues.image.data.src"
                    :alt="dataValues.image.data.alt"
                    class="md:ml-auto object-cover"
                />
            </EditableElement>
        </div>
    </div>
</template>
